import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Card, Tabs, DatePicker, Select, InputNumber, Space, Table, Radio, Button, Alert, Spin, Tag, Divider } from 'antd';
import { CalendarOutlined, ReloadOutlined, DollarOutlined, BankOutlined, LineChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';
import { computeForecast, aggregateByPeriod, expandMonthlyDrivers, PURCHASE_TERMS } from '../utils/financialPlanning';
import ShinehubPlanner from './ShinehubPlanner';

// ============================================================================
// Entity registry — extend here to add more entities
// ============================================================================
const ENTITIES = [
  { key: 'pw', label: 'PW Renewable' },
  { key: 'sh', label: 'ShineHub Residential' },
];

const HORIZON_OPTIONS = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
  { value: 24, label: '24 months' },
];

const PURCHASE_LABELS = {
  A: `10% deposit + ${PURCHASE_TERMS.A.shippingDays}d shipping + ${PURCHASE_TERMS.A.balanceDaysAfterShipping}d after shipping balance`,
  B: `20% deposit + ${PURCHASE_TERMS.B.shippingDays}d shipping + ${PURCHASE_TERMS.B.balanceDaysAfterShipping}d after shipping balance`,
  C: `20% deposit + ${PURCHASE_TERMS.C.shippingDays}d shipping + ${PURCHASE_TERMS.C.balanceDaysAfterShipping}d after shipping balance`,
};

function fmt(v) {
  if (v == null || isNaN(v)) return '-';
  const n = Number(v);
  const abs = Math.abs(n);
  const formatted = '$' + Math.round(abs).toLocaleString();
  if (n < 0) return <span style={{ color: '#cf1322' }}>-{formatted}</span>;
  return formatted;
}

// Format with thousand separators on integer part; cap to 2 decimal places.
const moneyFmt = (v) => {
  if (v == null || v === '') return '';
  const num = parseFloat(v);
  if (isNaN(num)) return String(v);
  const rounded = Math.round(num * 100) / 100;
  const parts = rounded.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return '$ ' + parts.join('.');
};
const moneyParse = (v) => v.replace(/\$\s?|,/g, '');

// ============================================================================
// Per-entity planning view
// ============================================================================
function EntityPlanner({ entity }) {
  const [startMonth, setStartMonth] = useState(dayjs());
  const [horizonMonths, setHorizonMonths] = useState(6);
  const [granularity, setGranularity] = useState('monthly');

  // Opening balances (fetched from Xero, editable)
  const [opening, setOpening] = useState({
    cash: 0,
    accountsReceivable: 0,
    inventory: 0,
    depositsToSuppliers: 0,
    otherAssets: 0,
    accountsPayable: 0,
    otherLiabilities: 0,
    gstPayable: 0,
  });
  const [openingAsOf, setOpeningAsOf] = useState(null);
  const [openingLoading, setOpeningLoading] = useState(false);
  const [openingError, setOpeningError] = useState(null);

  // OpEx baseline + revenue + GP% (fetched from Xero, editable)
  const [monthlyOpex, setMonthlyOpex] = useState(0);
  const [baselineDetail, setBaselineDetail] = useState([]);
  const [baselineRevenue, setBaselineRevenue] = useState(null);
  const [baselineGpPct, setBaselineGpPct] = useState(null);
  const [opexLoading, setOpexLoading] = useState(false);
  const [driversPristine, setDriversPristine] = useState(true);
  const [cogsPristine, setCogsPristine] = useState(true);

  // Outstanding invoices (existing AR/AP from Xero)
  const [arInvoices, setArInvoices] = useState([]);
  const [apInvoices, setApInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(null);
  // Excluded contact names (filter out from forecast + display)
  const [arExcluded, setArExcluded] = useState([]);
  const [apExcluded, setApExcluded] = useState([]);
  const [filtersInitialised, setFiltersInitialised] = useState(false);
  // Due date display filter (table only, not forecast)
  const [arDateRange, setArDateRange] = useState(null);
  const [apDateRange, setApDateRange] = useState(null);

  // COGS margin
  const [cogsPct, setCogsPct] = useState(0.75); // 75% COGS / 25% gross margin default

  // Driver inputs: 3 explicit months, rest extrapolate
  const [drivers, setDrivers] = useState([
    { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 },
    { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 },
    { sales: 0, purchaseA: 0, purchaseB: 0, purchaseC: 0 },
  ]);

  const updateDriver = (monthIdx, key, value) => {
    setDriversPristine(false);
    setDrivers(prev => prev.map((d, i) => i === monthIdx ? { ...d, [key]: value || 0 } : d));
  };

  // Fetch opening balance from Xero
  const fetchOpening = useCallback(async () => {
    setOpeningLoading(true);
    setOpeningError(null);
    try {
      const res = await api.get('/api/financial-planning/opening', {
        params: { entity, month: startMonth.format('YYYY-MM') },
      });
      const d = res.data.data;
      setOpening({
        cash: d.cash || 0,
        accountsReceivable: d.accountsReceivable || 0,
        inventory: d.inventory || 0,
        depositsToSuppliers: d.depositsToSuppliers || 0,
        otherAssets: d.otherAssets || 0,
        accountsPayable: d.accountsPayable || 0,
        otherLiabilities: d.otherLiabilities || 0,
        gstPayable: d.gstPayable || 0,
      });
      setOpeningAsOf(d.asOfDate || null);
    } catch (err) {
      setOpeningError(err.response?.data?.message || 'Failed to load opening balance from Xero');
    }
    setOpeningLoading(false);
  }, [entity, startMonth]);

  // Fetch opex baseline + revenue + GP% from Xero
  const fetchOpex = useCallback(async () => {
    setOpexLoading(true);
    try {
      const res = await api.get('/api/financial-planning/opex-baseline', {
        params: { entity, month: startMonth.format('YYYY-MM') },
      });
      const d = res.data.data;
      setMonthlyOpex(d.monthlyOpex || 0);
      setBaselineDetail(d.detail || []);
      setBaselineRevenue(d.monthlyRevenue || 0);
      setBaselineGpPct(d.avgGpPct);
      // Pre-fill drivers and COGS% on first load (don't override user edits)
      if (driversPristine && d.monthlyRevenue) {
        setDrivers(prev => prev.map(row => ({ ...row, sales: d.monthlyRevenue })));
      }
      if (cogsPristine && d.avgGpPct != null) {
        setCogsPct(Math.max(0, Math.min(1, (100 - d.avgGpPct) / 100)));
      }
    } catch {
      // silent — user can still enter manually
    }
    setOpexLoading(false);
  }, [entity, startMonth, driversPristine, cogsPristine]);

  // Fetch outstanding invoices (existing AR/AP)
  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const res = await api.get('/api/financial-planning/invoices', { params: { entity } });
      const d = res.data.data;
      setArInvoices(d.ar || []);
      setApInvoices(d.ap || []);
    } catch (err) {
      setInvoicesError(err.response?.data?.message || 'Failed to load invoices from Xero');
      setArInvoices([]);
      setApInvoices([]);
    }
    setInvoicesLoading(false);
  }, [entity]);

  useEffect(() => { fetchOpening(); fetchOpex(); fetchInvoices(); }, [fetchOpening, fetchOpex, fetchInvoices]);

  // Default exclusions: any contact whose name matches these patterns
  const DEFAULT_EXCLUSION_PATTERNS = [/alpha\s*ess/i, /kehua/i, /jspdi/i, /cleanergy/i];

  useEffect(() => {
    if (filtersInitialised) return;
    if (arInvoices.length === 0 && apInvoices.length === 0) return;
    const matchExcluded = (name) => DEFAULT_EXCLUSION_PATTERNS.some(p => p.test(name || ''));
    const arNames = [...new Set(arInvoices.map(i => i.contactName).filter(Boolean))];
    const apNames = [...new Set(apInvoices.map(i => i.contactName).filter(Boolean))];
    setArExcluded(arNames.filter(matchExcluded));
    setApExcluded(apNames.filter(matchExcluded));
    setFiltersInitialised(true);
  }, [arInvoices, apInvoices, filtersInitialised]);

  // Default date range: [startMonth - 2 months, startMonth + 3 months end]
  useEffect(() => {
    const from = startMonth.subtract(2, 'month').startOf('month');
    const to = startMonth.add(3, 'month').endOf('month');
    setArDateRange([from, to]);
    setApDateRange([from, to]);
  }, [startMonth]);

  // Customer-filtered invoices (used by forecast engine — date filter is display only)
  const arFiltered = useMemo(
    () => arInvoices.filter(inv => !arExcluded.includes(inv.contactName)),
    [arInvoices, arExcluded]
  );
  const apFiltered = useMemo(
    () => apInvoices.filter(inv => !apExcluded.includes(inv.contactName)),
    [apInvoices, apExcluded]
  );

  // Display-filtered invoices (customer filter + date filter)
  const dateInRange = (dateStr, range) => {
    if (!range || !range[0] || !range[1]) return true;
    if (!dateStr) return false;
    const d = dayjs(dateStr);
    return d.isAfter(range[0].subtract(1, 'day')) && d.isBefore(range[1].add(1, 'day'));
  };
  const arForDisplay = useMemo(
    () => arFiltered.filter(inv => dateInRange(inv.dueDate, arDateRange)),
    [arFiltered, arDateRange]
  );
  const apForDisplay = useMemo(
    () => apFiltered.filter(inv => dateInRange(inv.dueDate, apDateRange)),
    [apFiltered, apDateRange]
  );

  // Compute forecast (returns days array)
  const forecast = useMemo(() => {
    const expanded = expandMonthlyDrivers(drivers, horizonMonths);
    return computeForecast({
      openingBalances: opening,
      monthlyDrivers: expanded,
      cogsPct,
      monthlyOpex,
      horizonMonths,
      existingAR: arFiltered,
      existingAP: apFiltered,
      startDate: startMonth.startOf('month').format('YYYY-MM-DD'),
    });
  }, [drivers, horizonMonths, opening, cogsPct, monthlyOpex, arFiltered, apFiltered, startMonth]);

  // Aggregate days to weekly (Mon-Sun) or monthly periods
  const rows = useMemo(() => {
    return aggregateByPeriod(forecast.days, granularity === 'weekly' ? 'week' : 'month');
  }, [forecast, granularity]);

  // Build transposed tables: rows = line items, cols = periods
  const buildTable = (lineItems, dataKey) => {
    const periodCols = rows.map((r, i) => ({
      title: r.label,
      dataIndex: `col${i}`,
      align: 'right',
      width: 110,
    }));
    const tableData = lineItems.map(({ key, label, bold }) => {
      const row = { key, _label: label, _bold: bold };
      rows.forEach((r, i) => {
        row[`col${i}`] = r[dataKey][key];
      });
      return row;
    });
    const columns = [
      { title: '', dataIndex: '_label', width: 200, fixed: 'left', render: (v, rec) => rec._bold ? <b>{v}</b> : v },
      ...periodCols,
    ];
    return { columns, tableData };
  };

  const plItems = [
    { key: 'revenue', label: 'Revenue' },
    { key: 'cogs', label: 'Cost of Goods Sold' },
    { key: 'gp', label: 'Gross Profit', bold: true },
    { key: 'opex', label: 'Operating Expenses' },
    { key: 'np', label: 'Net Profit', bold: true },
  ];

  const bsItems = [
    { key: 'cash', label: 'Cash' },
    { key: 'ar', label: 'Accounts Receivable (incl GST)' },
    { key: 'inventory', label: 'Inventory' },
    { key: 'deposits', label: 'Deposits to Suppliers' },
    { key: 'otherAssets', label: 'Xero Other Assets' },
    { key: 'totalAssets', label: 'Total Assets', bold: true },
    { key: 'ap', label: 'Accounts Payable (incl GST)' },
    { key: 'gstPayable', label: 'Net GST Payable' },
    { key: 'otherLiabilities', label: 'Xero Other Liabilities' },
    { key: 'totalLiabilities', label: 'Total Liabilities', bold: true },
    { key: 'netAssets', label: 'Net Assets', bold: true },
    { key: 'retainedEarnings', label: 'Cumulative Net Profit' },
  ];

  const cfItems = [
    { key: 'cashIn', label: 'Cash In (from sales)' },
    { key: 'cashOutPurchases', label: 'Cash Out (purchases)' },
    { key: 'cashOutOpex', label: 'Cash Out (opex)' },
    { key: 'netCf', label: 'Net Cashflow', bold: true },
    { key: 'closingCash', label: 'Closing Cash Balance', bold: true },
  ];

  const pl = buildTable(plItems, 'pl');
  const bs = buildTable(bsItems, 'bs');
  const cf = buildTable(cfItems, 'cf');

  const renderCell = (v, rec) => {
    if (rec._bold) return <b>{fmt(v)}</b>;
    return fmt(v);
  };

  const applyRenderToColumns = (cols) =>
    cols.map(c => c.dataIndex === '_label' ? c : { ...c, render: renderCell });

  return (
    <>
      {/* Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <span style={{ fontSize: 12, color: '#666' }}>Start month:</span>
          <DatePicker
            picker="month"
            value={startMonth}
            onChange={d => d && setStartMonth(d)}
            format="MMM YYYY"
            size="small"
            allowClear={false}
          />
          <span style={{ fontSize: 12, color: '#666' }}>Horizon:</span>
          <Select value={horizonMonths} onChange={setHorizonMonths} options={HORIZON_OPTIONS} size="small" style={{ width: 110 }} />
          <span style={{ fontSize: 12, color: '#666' }}>View:</span>
          <Radio.Group value={granularity} onChange={e => setGranularity(e.target.value)} size="small" optionType="button" buttonStyle="solid">
            <Radio.Button value="monthly">Monthly</Radio.Button>
            <Radio.Button value="weekly">Weekly</Radio.Button>
          </Radio.Group>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { fetchOpening(); fetchOpex(); fetchInvoices(); }}>
            Refresh Xero
          </Button>
        </Space>
      </Card>

      {openingError && (
        <Alert message={openingError} type="warning" showIcon closable style={{ marginBottom: 16 }} onClose={() => setOpeningError(null)} />
      )}

      {/* Opening Balances */}
      <Card
        title={
          <>
            <BankOutlined /> Opening Balances (from Xero)
            {openingAsOf && (
              <Tag color="blue" style={{ marginLeft: 8, fontWeight: 'normal' }}>
                as of {dayjs(openingAsOf).format('D MMM YYYY')}
              </Tag>
            )}
          </>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={openingLoading}>
          {(() => {
            const Field = ({ label, k }) => (
              <Col span={6}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
                <InputNumber
                  value={opening[k]}
                  onChange={v => setOpening(p => ({ ...p, [k]: v || 0 }))}
                  style={{ width: '100%' }}
                  formatter={moneyFmt}
                  parser={moneyParse}
                />
              </Col>
            );
            const totalAssets = (opening.cash || 0) + (opening.accountsReceivable || 0) + (opening.inventory || 0) + (opening.depositsToSuppliers || 0) + (opening.otherAssets || 0);
            const totalLiab = (opening.accountsPayable || 0) + (opening.gstPayable || 0) + (opening.otherLiabilities || 0);
            return (
              <>
                <div style={{ fontSize: 12, color: '#54A2CC', fontWeight: 600, margin: '4px 0 8px' }}>Assets</div>
                <Row gutter={[16, 12]}>
                  <Field label="Cash" k="cash" />
                  <Field label="Accounts Receivable" k="accountsReceivable" />
                  <Field label="Inventory" k="inventory" />
                  <Field label="Deposits to Suppliers" k="depositsToSuppliers" />
                  <Field label="Other Assets (Xero)" k="otherAssets" />
                </Row>
                <div style={{ fontSize: 12, color: '#54A2CC', fontWeight: 600, margin: '12px 0 8px' }}>Liabilities</div>
                <Row gutter={[16, 12]}>
                  <Field label="Accounts Payable" k="accountsPayable" />
                  <Field label="Net GST Payable" k="gstPayable" />
                  <Field label="Other Liabilities (Xero)" k="otherLiabilities" />
                </Row>
                <div style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
                  Total Assets: <b>${Math.round(totalAssets).toLocaleString()}</b>
                  {' · '}Total Liabilities: <b>${Math.round(totalLiab).toLocaleString()}</b>
                  {' · '}Net Assets: <b>${Math.round(totalAssets - totalLiab).toLocaleString()}</b>
                </div>
              </>
            );
          })()}
        </Spin>
      </Card>

      {/* Assumptions */}
      <Card title="Assumptions" size="small" style={{ marginBottom: 16 }}>
        <Spin spinning={opexLoading}>
          <Row gutter={16}>
            <Col span={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>COGS as % of Revenue</div>
              <InputNumber value={cogsPct * 100} onChange={v => { setCogsPristine(false); setCogsPct((v || 0) / 100); }} style={{ width: '100%' }} min={0} max={100} step={0.1} formatter={v => `${v}%`} parser={v => v.replace('%', '')} />
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                Gross margin: {((1 - cogsPct) * 100).toFixed(1)}%
                {baselineGpPct != null && <> · 3-mo avg GP%: {baselineGpPct}%</>}
              </div>
            </Col>
            <Col span={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Monthly OpEx (avg last 3 months)</div>
              <InputNumber value={monthlyOpex} onChange={v => setMonthlyOpex(v || 0)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              {baselineDetail.length > 0 && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {baselineDetail.map(d => `${d.period}: $${d.opex.toLocaleString()}`).join(' · ')}
                </div>
              )}
            </Col>
            <Col span={8}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>3-mo avg revenue (used to pre-fill drivers)</div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>
                {baselineRevenue != null ? `$${Math.round(baselineRevenue).toLocaleString()}` : '-'}
              </div>
              {baselineDetail.length > 0 && (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {baselineDetail.map(d => `${d.period}: $${d.revenue.toLocaleString()}`).join(' · ')}
                </div>
              )}
            </Col>
          </Row>
        </Spin>
      </Card>

      {/* Drivers */}
      <Card title="Drivers (first 3 months — extrapolated flat after)" size="small" style={{ marginBottom: 16 }}>
        <Table
          size="small"
          pagination={false}
          dataSource={drivers.map((d, i) => ({ key: i, ...d, monthLabel: startMonth.add(i, 'month').format('MMM YYYY') }))}
          columns={[
            { title: 'Month', dataIndex: 'monthLabel', width: 100, fixed: 'left' },
            {
              title: 'Sales (1-mo terms)',
              dataIndex: 'sales',
              render: (v, rec) => (
                <InputNumber value={v} onChange={val => updateDriver(rec.key, 'sales', val)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              ),
            },
            {
              title: 'Purchase A',
              dataIndex: 'purchaseA',
              render: (v, rec) => (
                <InputNumber value={v} onChange={val => updateDriver(rec.key, 'purchaseA', val)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              ),
            },
            {
              title: 'Purchase B',
              dataIndex: 'purchaseB',
              render: (v, rec) => (
                <InputNumber value={v} onChange={val => updateDriver(rec.key, 'purchaseB', val)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              ),
            },
            {
              title: 'Purchase C',
              dataIndex: 'purchaseC',
              render: (v, rec) => (
                <InputNumber value={v} onChange={val => updateDriver(rec.key, 'purchaseC', val)} style={{ width: '100%' }} formatter={moneyFmt} parser={moneyParse} />
              ),
            },
          ]}
        />
        <div style={{ fontSize: 11, color: '#888', marginTop: 8, lineHeight: 1.6 }}>
          <b>Purchase A:</b> {PURCHASE_LABELS.A}<br />
          <b>Purchase B:</b> {PURCHASE_LABELS.B}<br />
          <b>Purchase C:</b> {PURCHASE_LABELS.C}
        </div>
      </Card>

      {/* Forecast Output Tables */}
      <Card title={<><LineChartOutlined /> Forecast P&L</>} size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={pl.tableData}
          columns={applyRenderToColumns(pl.columns)}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={r => r._bold ? 'xero-key-row' : ''}
        />
      </Card>

      <Card title={<><BankOutlined /> Forecast Balance Sheet</>} size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={bs.tableData}
          columns={applyRenderToColumns(bs.columns)}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={r => r._bold ? 'xero-key-row' : ''}
        />
      </Card>

      <Card title={<><DollarOutlined /> Forecast Cashflow</>} size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={cf.tableData}
          columns={applyRenderToColumns(cf.columns)}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={r => r._bold ? 'xero-key-row' : ''}
        />
      </Card>

      {/* Outstanding Invoices */}
      {invoicesError && (
        <Alert message={invoicesError} type="warning" showIcon closable style={{ marginBottom: 16 }} onClose={() => setInvoicesError(null)} />
      )}
      {(() => {
        const arNames = [...new Set(arInvoices.map(i => i.contactName).filter(Boolean))].sort();
        const arTotalAmount = arForDisplay.reduce((s, i) => s + (i.total || 0), 0);
        const arTotalDue = arForDisplay.reduce((s, i) => s + (i.amountDue || 0), 0);
        return (
          <Card
            title={
              <span>
                Outstanding AR
                <Tag color="green" style={{ marginLeft: 8 }}>
                  ${Math.round(arTotalDue).toLocaleString()} due
                </Tag>
                <Tag style={{ marginLeft: 4 }}>{arForDisplay.length} of {arInvoices.length}</Tag>
              </span>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Space wrap size="small" style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Exclude customer:</span>
              <Select
                mode="multiple"
                placeholder="None"
                value={arExcluded}
                onChange={setArExcluded}
                style={{ minWidth: 280, maxWidth: 420 }}
                size="small"
                maxTagCount={1}
                maxTagPlaceholder={(omitted) => `Other ${omitted.length} customer${omitted.length > 1 ? 's' : ''}`}
                options={arNames.map(n => ({ label: n, value: n }))}
                allowClear
              />
              <span style={{ fontSize: 12, color: '#888', marginLeft: 12 }}>Due date:</span>
              <DatePicker.RangePicker
                picker="month"
                value={arDateRange}
                onChange={setArDateRange}
                size="small"
                allowClear
                format="MMM YYYY"
              />
            </Space>
            <Spin spinning={invoicesLoading}>
              <Table
                size="small"
                dataSource={arForDisplay.map((inv, i) => ({ ...inv, key: i }))}
                pagination={{ pageSize: 15, size: 'small' }}
                columns={[
                  { title: 'Customer', dataIndex: 'contactName', ellipsis: true, width: 200 },
                  { title: 'Invoice #', dataIndex: 'invoiceNumber', width: 170 },
                  { title: 'Due', dataIndex: 'dueDate', width: 130, render: v => v ? dayjs(v).format('D MMM YYYY') : '-' },
                  { title: 'Amount', dataIndex: 'total', width: 140, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
                  { title: 'Outstanding', dataIndex: 'amountDue', width: 150, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}><b>Total ({arForDisplay.length})</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right"><b>${Math.round(arTotalAmount).toLocaleString()}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><b>${Math.round(arTotalDue).toLocaleString()}</b></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Spin>
          </Card>
        );
      })()}

      {(() => {
        const apNames = [...new Set(apInvoices.map(i => i.contactName).filter(Boolean))].sort();
        const apTotalAmount = apForDisplay.reduce((s, i) => s + (i.total || 0), 0);
        const apTotalDue = apForDisplay.reduce((s, i) => s + (i.amountDue || 0), 0);
        return (
          <Card
            title={
              <span>
                Outstanding AP
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  ${Math.round(apTotalDue).toLocaleString()} due
                </Tag>
                <Tag style={{ marginLeft: 4 }}>{apForDisplay.length} of {apInvoices.length}</Tag>
              </span>
            }
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Space wrap size="small" style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Exclude supplier:</span>
              <Select
                mode="multiple"
                placeholder="None"
                value={apExcluded}
                onChange={setApExcluded}
                style={{ minWidth: 280, maxWidth: 420 }}
                size="small"
                maxTagCount={1}
                maxTagPlaceholder={(omitted) => `Other ${omitted.length} supplier${omitted.length > 1 ? 's' : ''}`}
                options={apNames.map(n => ({ label: n, value: n }))}
                allowClear
              />
              <span style={{ fontSize: 12, color: '#888', marginLeft: 12 }}>Due date:</span>
              <DatePicker.RangePicker
                picker="month"
                value={apDateRange}
                onChange={setApDateRange}
                size="small"
                allowClear
                format="MMM YYYY"
              />
            </Space>
            <Spin spinning={invoicesLoading}>
              <Table
                size="small"
                dataSource={apForDisplay.map((inv, i) => ({ ...inv, key: i }))}
                pagination={{ pageSize: 15, size: 'small' }}
                columns={[
                  { title: 'Supplier', dataIndex: 'contactName', ellipsis: true, width: 200 },
                  { title: 'Bill #', dataIndex: 'invoiceNumber', width: 170 },
                  { title: 'Due', dataIndex: 'dueDate', width: 130, render: v => v ? dayjs(v).format('D MMM YYYY') : '-' },
                  { title: 'Amount', dataIndex: 'total', width: 140, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
                  { title: 'Outstanding', dataIndex: 'amountDue', width: 150, align: 'right', render: v => `$${Math.round(v || 0).toLocaleString()}` },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}><b>Total ({apForDisplay.length})</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right"><b>${Math.round(apTotalAmount).toLocaleString()}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><b>${Math.round(apTotalDue).toLocaleString()}</b></Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Spin>
          </Card>
        );
      })()}
    </>
  );
}

// ============================================================================
// Main Page
// ============================================================================
export default function FinancialPlanning() {
  const tabItems = ENTITIES.map(e => ({
    key: e.key,
    label: e.label,
    children: e.key === 'sh' ? <ShinehubPlanner /> : <EntityPlanner entity={e.key} />,
  }));

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Financial Planning</h1>
        <p>Forecast P&L, Balance Sheet, and Cashflow driven by sales and purchase assumptions</p>
      </div>

      <Tabs defaultActiveKey={ENTITIES[0]?.key} items={tabItems} />
    </PageLayout>
  );
}
